// Runtime Dynamic Client Registration (RFC 7591) for the public SPA client.
// Only the public `client_id` is ever stored; a public client receives no secret.

import {
  CLIENT_NAME,
  OAUTH_CLIENT_ID,
  OAUTH_SCOPE,
  oauthRegisterUrl,
  redirectUri,
} from 'services/auth/constants';

export type OAuthClientRegistrationErrorCode = 'invalid_request' | 'rate_limited' | 'network' | 'unknown';

export class OAuthClientRegistrationError extends Error {
  code: OAuthClientRegistrationErrorCode;

  status?: number;

  constructor(code: OAuthClientRegistrationErrorCode, message: string, status?: number) {
    super(message);
    this.name = 'OAuthClientRegistrationError';
    this.code = code;
    this.status = status;
  }
}

const failureFor = (status: number): OAuthClientRegistrationError => {
  if (status === 400) {
    return new OAuthClientRegistrationError('invalid_request', 'Client registration rejected (400)', status);
  }
  if (status === 429) {
    return new OAuthClientRegistrationError('rate_limited', 'Client registration rate limited (429)', status);
  }
  return new OAuthClientRegistrationError('unknown', `Client registration failed (${status})`, status);
};

const register = async (): Promise<string> => {
  let response: Response;
  try {
    response = await fetch(oauthRegisterUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        client_name: CLIENT_NAME,
        redirect_uris: [redirectUri()],
        token_endpoint_auth_method: 'none',
        grant_types: ['authorization_code', 'refresh_token'],
        response_types: ['code'],
        scope: OAUTH_SCOPE,
      }),
    });
  } catch {
    throw new OAuthClientRegistrationError('network', 'Client registration request failed');
  }

  if (!response.ok) throw failureFor(response.status);

  let clientId: unknown;
  try {
    clientId = (await response.json())?.client_id;
  } catch {
    throw new OAuthClientRegistrationError('unknown', 'Client registration returned an unreadable body', response.status);
  }
  if (typeof clientId !== 'string' || clientId === '') {
    throw new OAuthClientRegistrationError('unknown', 'Client registration returned no client_id', response.status);
  }
  return clientId;
};

export const ensureClientId = async (): Promise<string> => {
  const stored = localStorage.getItem(OAUTH_CLIENT_ID);
  if (stored) return stored;

  const clientId = await register();
  localStorage.setItem(OAUTH_CLIENT_ID, clientId);
  return clientId;
};

export const resetClientId = (): void => {
  localStorage.removeItem(OAUTH_CLIENT_ID);
};
